import * as yup from "yup";
import { useInfiniteQuery } from "@tanstack/react-query";
import { HTTP_ENDPOINTS, QUERY_KEYS } from "../../consts/api";
import { http } from "../../services/HttpService";
import { removeNullishFromShape, unwrapOr } from "../../utilities/general";
import {
  composePaginationSchema,
  getNextPaginationPage,
  paginationSchema,
} from "../../schemas/pagination";
import { createQueryKeyFromShape } from "../../utilities/api";

const requestSchema = yup.object({
  pageSize: yup.number().required(),

  period: yup
    .object({
      from: yup.string().nullable(),
    })
    .nullable(),
});

const responseSchema = paginationSchema.concat(
  yup.object({
    data: yup
      .array()
      .of(
        yup.object({
          id: yup.number().required(),
          patient: yup
            .object({
              id: yup.number().required(),
              full_name: yup.string().required(),
            })
            .nullable(),
          pages: yup.number().required(),
          direction: yup.number().required(),
          fax_number: yup.string().required(),
          created_at: yup.string().required(),
          status: yup.number().required(),
        }),
      )
      .required(),
    totalPages: yup.number().required(),
    remainingPages: yup.number().required(),
    drawDay: yup.number().required(),
  }),
);

const composeResponse = (res, req) => {
  const data = res?.data?.data || {};

  return {
    ...composePaginationSchema({
      response: res?.data?.data || {},
      pageSize: req.pageSize,
    }),
    data: data.log?.data || [],
    totalPages: Number(data.used) || 0,
    remainingPages: Number(data.remaining) || 0,
    drawDay: data.draw_day,
  };
};

const createEfaxUsageLogQueryKey = (...args) => [
  QUERY_KEYS.efax.usageLog,
  ...args,
];

const createKeyFromShape = (shape) =>
  unwrapOr(() => createQueryKeyFromShape(removeNullishFromShape(shape)), null);

export function useEfaxUsageLogQuery({ payload = {}, options = {} } = {}) {
  const req = requestSchema.validateSync(payload, {
    strict: true,
  });

  return useInfiniteQuery({
    queryKey: createEfaxUsageLogQueryKey(
      createKeyFromShape(payload.period),
      req.pageSize,
    ),
    queryFn: async ({ pageParam = 1 }) => {
      const res = await http.get(HTTP_ENDPOINTS.efax.getUsageLog(), {
        params: removeNullishFromShape({
          page: pageParam,
          pagesize: req.pageSize,
          start_date: req.period.from,
        }),
      });
      return responseSchema.validateSync(composeResponse(res, req), {
        strict: true,
      });
    },
    ...options,
    getNextPageParam: getNextPaginationPage,
  });
}
